import React from 'react';
import {Text, View} from '@react-pdf/renderer';
import {IntlShape} from 'react-intl';
import {styles, ViewLine, ViewRow} from './InvoicePDFLayout';
import {ViewColumn} from '../../../../../components/initial-plan/pdf/layout/PDFLayout';
import {workDetailUngroupedLabelKey} from '../../../../../components/insights/invoicing/line-generation/invoicing_groupings';
import Util from '../../../../../forecast-app/shared/util/util';
import type {InvoicePDFDownloadModal_viewer$data as InvoicePDFDownloadModal_viewer} from './__generated__/InvoicePDFDownloadModal_viewer.graphql';

const LABEL_WIDTH = '46%';
const HOURS_WIDTH = '12%';
const DATES_WIDTH = '24%';
const RATE_WIDTH = '18%';
const VALUE_WIDTH = '18%';
const LABEL_WIDTH_WITH_RATES = '28%';

/**
 * Printed wherever rate disclosure is on but the row has no money snapshotted against it.
 *
 * Rate and value are snapshotted per row when the invoice is saved, so that a later rate-card change
 * cannot retroactively alter a document already sent. The company setting that adds the two columns
 * is read live, and the two are independent: turning disclosure on after invoices exist gives those
 * older snapshots the headers with nothing to put under them, and a deliverable billed with no time
 * logged against it has nothing to price even on a freshly-priced invoice. A blank cell on a
 * client-facing document reads as a broken document; this reads as "not recorded".
 */
const NO_AMOUNT = '—';

type InvoiceData = InvoicePDFDownloadModal_viewer['invoice'];

type WorkDetailPdfRow = {
	id: string;
	groupLabel: string | null;
	sortOrder: number | null;
	minutes: number | null;
	firstYear: number | null;
	firstMonth: number | null;
	firstDay: number | null;
	lastYear: number | null;
	lastMonth: number | null;
	lastDay: number | null;
	rate: number | null;
	value: number | null;
	removed: boolean | null;
};

type InvoicePDFWorkDetailProps = {
	invoiceData: InvoiceData;
	intl: IntlShape;
	showRates: boolean;
	quantityDecimals: number;
};

/**
 * The rows that will actually print: the operator's removals dropped, in the order the snapshot was
 * persisted in.
 *
 * Removed rows are persisted rather than deleted so a removal survives reopening the invoice, which
 * means every consumer that renders has to filter them out itself.
 */
export const visibleWorkDetailRows = (invoiceData: InvoiceData): WorkDetailPdfRow[] => {
	if (!invoiceData || !invoiceData.includeWorkDetail) return [];
	const edges = invoiceData.workDetailRows?.edges ?? [];
	return edges
		.map(edge => edge?.node)
		.filter((node): node is WorkDetailPdfRow => !!node && !node.removed)
		.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
};

const formatDate = (intl: IntlShape, year: number | null, month: number | null, day: number | null): string | null => {
	if (!year || !month || !day) return null;
	return intl.formatDate(new Date(year, month - 1, day), {year: 'numeric', month: 'short', day: 'numeric'});
};

const dateRange = (intl: IntlShape, row: WorkDetailPdfRow): string => {
	const first = formatDate(intl, row.firstYear, row.firstMonth, row.firstDay);
	const last = formatDate(intl, row.lastYear, row.lastMonth, row.lastDay);
	if (!first) return '';
	return last && last !== first ? `${first} - ${last}` : first;
};

const hoursOf = (minutes: number | null, quantityDecimals: number): number => {
	const factor = Math.pow(10, quantityDecimals);
	return Math.round(((minutes ?? 0) / 60) * factor) / factor;
};

/**
 * The work behind a fixed-price invoice, printed below Balance due as supporting detail.
 *
 * Returns null rather than an empty section when there is nothing to show — a 100%-upfront invoice
 * raised before any work exists is the common case, and an empty table on a client-facing document
 * reads as an error.
 *
 * Money appears ONLY when the company has opted in to rate disclosure. Even then the section states
 * what was done rather than what is charged: the invoice total above is unaffected by anything here,
 * and the caption directly under the heading is what keeps the section from being read as a second
 * set of charges — it is not conditional.
 *
 * The rows render as one flat list, in the order the snapshot recorded them.
 */
const InvoicePDFWorkDetail = ({
	invoiceData,
	intl,
	showRates,
	quantityDecimals,
}: InvoicePDFWorkDetailProps): JSX.Element | null => {
	const rows = visibleWorkDetailRows(invoiceData);
	if (rows.length === 0) return null;

	/**
	 * A null label means the grouping dimension was absent for that row's time, so the wording comes
	 * from the dimension the snapshot was built with, which the invoice records alongside the rows.
	 */
	const rowLabel = (row: WorkDetailPdfRow): string | null =>
		row.groupLabel == null
			? intl.formatMessage({id: workDetailUngroupedLabelKey(invoiceData?.workDetailGrouping ?? '')})
			: row.groupLabel;

	const labelWidth = showRates ? LABEL_WIDTH_WITH_RATES : LABEL_WIDTH;
	const currencySymbol = Util.GetCurrencySymbol(invoiceData?.currency);
	const money = (amount: number | null) => Util.getFormattedNumberWithCurrency(currencySymbol, amount ?? 0, intl);

	const totalHours = hoursOf(
		rows.reduce((sum, row) => sum + (row.minutes ?? 0), 0),
		quantityDecimals
	);
	const anyValues = rows.some(row => row.value != null);
	const totalValue = rows.reduce((sum, row) => sum + (row.value ?? 0), 0);

	const renderRow = (row: WorkDetailPdfRow) => (
		<ViewLine key={row.id}>
			<Text style={[styles.textMain, {width: labelWidth, paddingTop: '4px', paddingBottom: '4px'}]}>{rowLabel(row)}</Text>
			<Text style={[styles.textTableRight, {width: HOURS_WIDTH}]}>{hoursOf(row.minutes, quantityDecimals)}</Text>
			<Text style={[styles.textTableRight, {width: DATES_WIDTH}]}>{dateRange(intl, row)}</Text>
			{showRates ? (
				<Text style={[styles.textTableRight, {width: RATE_WIDTH}]}>
					{row.rate == null ? NO_AMOUNT : money(row.rate)}
				</Text>
			) : null}
			{showRates ? (
				<Text style={[styles.textTableRight, {width: VALUE_WIDTH}]}>
					{row.value == null ? NO_AMOUNT : money(row.value)}
				</Text>
			) : null}
		</ViewLine>
	);

	return (
		<View style={{paddingTop: '32px'}} wrap={true}>
			<Text style={[styles.textMainBold, {paddingBottom: '4px'}]}>
				{intl.formatMessage({id: 'invoicing.work_detail.section_title'})}
			</Text>
			<Text style={[styles.fromToText, {fontSize: '10px', paddingBottom: '8px'}]}>
				{intl.formatMessage({id: 'invoicing.work_detail.not_charged_caption'})}
			</Text>

			<ViewLine style={{alignItems: 'center', backgroundColor: '#D9D9D9', height: '24px'}}>
				<Text style={[styles.textMain, {width: labelWidth}]}>{intl.formatMessage({id: 'common.description'})}</Text>
				<Text style={[styles.textTableRight, {width: HOURS_WIDTH}]}>
					{intl.formatMessage({id: 'invoicing.work_detail.column_hours'})}
				</Text>
				<Text style={[styles.textTableRight, {width: DATES_WIDTH}]}>
					{intl.formatMessage({id: 'invoicing.work_detail.column_dates'})}
				</Text>
				{showRates ? (
					<Text style={[styles.textTableRight, {width: RATE_WIDTH}]}>
						{intl.formatMessage({id: 'invoicing.work_detail.column_rate'})}
					</Text>
				) : null}
				{showRates ? (
					<Text style={[styles.textTableRight, {width: VALUE_WIDTH}]}>
						{intl.formatMessage({id: 'invoicing.work_detail.column_value'})}
					</Text>
				) : null}
			</ViewLine>

			{rows.map(renderRow)}

			<ViewRow style={{justifyContent: 'flex-end', paddingTop: '8px', gap: '20px', paddingRight: '20px'}}>
				<ViewColumn>
					<Text style={styles.textSummary}>{intl.formatMessage({id: 'invoicing.work_detail.total_hours'})}:</Text>
					{showRates ? (
						<Text style={styles.textSummary}>{intl.formatMessage({id: 'invoicing.work_detail.total_value'})}:</Text>
					) : null}
				</ViewColumn>
				<ViewColumn style={{alignItems: 'flex-end'}}>
					<Text style={styles.textSummary}>{totalHours}</Text>
					{showRates ? <Text style={styles.textSummary}>{anyValues ? money(totalValue) : NO_AMOUNT}</Text> : null}
				</ViewColumn>
			</ViewRow>
		</View>
	);
};

export default InvoicePDFWorkDetail;
